import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteWorkspaceFile } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import type { FileListing } from '@/lib/api/generated/model';
import { getDeleteFailureMessage } from '../errors';
import { fileKeys } from '../queryKeys';

/**
 * Deletes a file and optimistically removes it from the current folder's
 * cached listing so the row disappears immediately, without waiting on the
 * invalidated refetch. `onError` restores the previous listing snapshot
 * (Principle 4 — optimistic updates require rollback), per data-model.md's
 * Relationships section.
 */
export function useDeleteWorkspaceFile(
  workspaceId: string,
  currentPath: string,
) {
  const queryClient = useQueryClient();
  const listingKey = fileKeys.listing(workspaceId, currentPath);

  return useMutation({
    mutationFn: async ({
      path,
      fileName,
    }: {
      path: string;
      fileName: string;
    }) =>
      expectStatus(
        await deleteWorkspaceFile(workspaceId, { path }),
        204,
        getDeleteFailureMessage(fileName),
      ),
    onMutate: async ({ path }: { path: string; fileName: string }) => {
      await queryClient.cancelQueries({ queryKey: listingKey });

      const previousListing = queryClient.getQueryData<FileListing | null>(
        listingKey,
      );

      if (previousListing) {
        const nextListing: FileListing = {
          ...previousListing,
          entries: previousListing.entries.filter(
            (entry) => entry.path !== path,
          ),
        };
        queryClient.setQueryData(listingKey, nextListing);
      }

      return { previousListing };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousListing !== undefined) {
        queryClient.setQueryData(listingKey, context.previousListing);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listingKey });
    },
  });
}
